import {Subscription} from "rxjs";
import {DatePipe} from "@angular/common";
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router, RouterLink} from "@angular/router";

import {ToastrService} from "ngx-toastr";

import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {RestoreScrollPositionDirective} from "@numizmat/directives";
import {ProfileFollowedUserApiService, UserFollowerApiService} from "@numizmat/services/core";
import {
  ProfileFollowedUserSortOptionApiService,
  ProfileFollowedUserSortOptionStateService
} from "@numizmat/services/nomenclature";
import {
  defaultPageLimit,
  ImageComponent,
  PaginationComponent,
  SearchInputComponent,
  SearchResultComponent,
  SortOption, UserRatingComponent
} from "@numizmat/components";
import {
  FilterProfileFollowedUserResponse,
  FilterProfileFollowedUsersRequest,
  FilterProfileFollowedUsersResponse
} from "@numizmat/services-models/core";

@Component({
  selector: 'nz-profile-followed-users',
  standalone: true,
  imports: [
    SearchInputComponent,
    SearchResultComponent,
    PaginationComponent,
    RestoreScrollPositionDirective,
    ImageComponent,
    RouterLink,
    DatePipe,
    UserRatingComponent
  ],
  templateUrl: './profile-followed-users.component.html',
  styleUrl: './profile-followed-users.component.scss'
})
export class ProfileFollowedUsersComponent implements OnInit, OnDestroy {
  @Input() targetMenuId!: string;

  private router: Router = inject(Router);
  private toastrService: ToastrService = inject(ToastrService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private userFollowerApiService: UserFollowerApiService = inject(UserFollowerApiService);
  private profileFollowedUserApiService: ProfileFollowedUserApiService = inject(ProfileFollowedUserApiService);
  private profileFollowedUserSortOptionApiService: ProfileFollowedUserSortOptionApiService = inject(ProfileFollowedUserSortOptionApiService);
  private profileFollowedUserSortOptionStateService: ProfileFollowedUserSortOptionStateService = inject(ProfileFollowedUserSortOptionStateService);

  private currentRoute: string = '';
  private subs: Subscription = new Subscription();

  protected sortOptions: SortOption[] = [];
  protected selectedSortOption: number = 1;
  protected loading: boolean = true;
  protected totalResults: number = 0;
  protected searchValue: string = '';
  protected selectedPage: number = 1;
  protected selectedPageLimitOptionId: number = defaultPageLimit;
  protected users: FilterProfileFollowedUserResponse[] = [];

  protected readonly defaultPageLimit = defaultPageLimit;
  protected readonly appRoutes = appRoutes;
  protected readonly environment = environment;

  public ngOnInit(): void {
    this.currentRoute = this.router.url.split('?')[0];
    this.getAllProfileFollowedUserSortOptions();
    this.createQueryParamsChangeSubscription();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onSort(sortOption: number): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {sortOption: sortOption, page: 1},
    }).then();
  }

  protected onSearch(searchValue: string): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {searchValue: searchValue, page: 1},
    }).then();
  }

  protected deleteUserFollower(followedUser: FilterProfileFollowedUserResponse): void {
    this.loading = true;
    this.userFollowerApiService.deleteUserFollower(followedUser.userFollowerId).subscribe({
      next: response => {
        this.filterProfileFollowedUsers();
        this.toastrService.success(`Успешно спряхте да следвате ${followedUser.username}!`);
      }
    });
  }

  private createQueryParamsChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.queryParams.subscribe({
      next: (queryParams: Params) => {
        const menuId = queryParams['menuId'];
        if (menuId !== this.targetMenuId) {
          return;
        }
        this.searchValue = queryParams['searchValue'];
        this.selectedPage = !!queryParams['page'] ? Number.parseInt(queryParams['page']) : 1;
        this.selectedPageLimitOptionId = !!queryParams['pageLimit'] ? Number.parseInt(queryParams['pageLimit']) : defaultPageLimit;
        this.selectedSortOption = !!queryParams['sortOption'] ? Number.parseInt(queryParams['sortOption']) : 1;
        this.filterProfileFollowedUsers();
      }
    });
    this.subs.add(subscription);
  }

  private filterProfileFollowedUsers(): void {
    this.loading = true;
    const request: FilterProfileFollowedUsersRequest = {
      searchValue: this.searchValue,
      selectedPage: this.selectedPage,
      selectedPageLimit: this.selectedPageLimitOptionId,
      selectedSortOption: this.selectedSortOption
    };
    this.profileFollowedUserApiService.filterProfileFollowedUsers(request).subscribe({
      next: (response: FilterProfileFollowedUsersResponse) => {
        this.totalResults = response.totalResults;
        this.users = response.users;
        this.loading = false;
      }
    });
  }

  private getAllProfileFollowedUserSortOptions(): void {
    const subscription: Subscription = this.profileFollowedUserSortOptionStateService.getAllProfileFollowedUserSortOptions().subscribe({
      next: profileFollowedUserSortOptions => {
        if (!!profileFollowedUserSortOptions) {
          this.sortOptions = profileFollowedUserSortOptions.map(x => {
            return {
              id: x.profileFollowedUserSortOption,
              name: x.profileFollowedUserSortOptionName
            }
          });
        } else {
          this.profileFollowedUserSortOptionApiService.getAllProfileFollowedUserSortOptions().subscribe({
            next: response => {
              this.profileFollowedUserSortOptionStateService.setAllProfileFollowedUserSortOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }
}
